import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  PlusCircle,
  Truck,
  MapPin,
  Calendar,
  Clock,
  Users,
  FileText,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  Building2,
  HelpCircle,
  Sparkles,
  Edit3
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { OrdemDeServico } from '../../types/ordemServico';
import { SelecaoVeiculoProposta, VeiculoPropostaOption } from '../../components/areaCliente/SelecaoVeiculoProposta';
import { AssinaturaDigitalPad } from '../../components/areaCliente/AssinaturaDigitalPad';
import { propostaSaldoService, PropostaSaldoInfo } from '../../services/propostaSaldoService';
import { PropostaSaldoBanner } from '../../components/ordemServico/PropostaSaldoBanner';

export function AreaClienteSolicitarFrete() {
  const { clienteAtual, refreshData } = useAreaCliente();
  const navigate = useNavigate();

  // Form states
  const [dataAtendimento, setDataAtendimento] = useState('');
  const [horarioInicio, setHorarioInicio] = useState('08:00');
  const [horarioFinal, setHorarioFinal] = useState('17:00');
  const [descricao, setDescricao] = useState('');
  
  // Addresses
  const [retirada, setRetirada] = useState('');
  const [entrega, setEntrega] = useState('');
  
  // Vehicle from proposal
  const [selectedProposalOption, setSelectedProposalOption] = useState<VeiculoPropostaOption | null>(null);
  const [selectedOptionKey, setSelectedOptionKey] = useState<string>('');
  const [quantidadeAjudante, setQuantidadeAjudante] = useState<number>(0);
  const [detalhesAdicional, setDetalhesAdicional] = useState('');
  const [observacoes, setObservacoes] = useState('');

  // Proposal Balance (Cota de Caminhões)
  const [saldoInfo, setSaldoInfo] = useState<PropostaSaldoInfo | null>(null);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [bloqueioSemSaldoAtivo, setBloqueioSemSaldoAtivo] = useState(false);

  // Signature state
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [nomeSignatario, setNomeSignatario] = useState(clienteAtual?.nome || '');
  const [documentoSignatario, setDocumentoSignatario] = useState(clienteAtual?.documento || '');

  // UI status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdOS, setCreatedOS] = useState<OrdemDeServico | null>(null);

  // Load global blocking configuration
  useEffect(() => {
    propostaSaldoService.isBloqueioSemSaldoAtivo().then(setBloqueioSemSaldoAtivo);
  }, []);

  // Set default date to tomorrow if empty
  useEffect(() => {
    if (!dataAtendimento) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDataAtendimento(tomorrow.toISOString().split('T')[0]);
    }
  }, [dataAtendimento]);

  useEffect(() => {
    if (clienteAtual) {
      setNomeSignatario(clienteAtual.nome || '');
      setDocumentoSignatario(clienteAtual.documento || '');
    }
  }, [clienteAtual]);

  const handleSelectProposalVehicle = async (option: VeiculoPropostaOption | null) => {
    setSelectedProposalOption(option);
    setSelectedOptionKey(option ? option.key : '');
    if (option && !descricao.trim()) {
      setDescricao(`Transporte via ${option.nomeVeiculo} (Proposta #${option.propostaNumero})`);
    }

    // Se selecionou uma proposta, consulta o saldo de caminhões e consumo
    if (option && option.pedidoId) {
      setLoadingSaldo(true);
      try {
        const saldo = await propostaSaldoService.getSaldoProposta(option.pedidoId);
        setSaldoInfo(saldo);
      } catch (err) {
        console.error('Erro ao verificar saldo da proposta:', err);
      } finally {
        setLoadingSaldo(false);
      }
    } else {
      setSaldoInfo(null);
    }
  };

  const handleSignatureChange = (base64: string | null, nome: string, doc: string) => {
    setSignatureBase64(base64);
    setNomeSignatario(nome);
    setDocumentoSignatario(doc);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clienteAtual) {
      setErrorMessage('Nenhum cliente selecionado.');
      return;
    }

    if (!dataAtendimento) {
      setErrorMessage('Informe a data de atendimento desejada.');
      return;
    }

    if (!retirada.trim()) {
      setErrorMessage('Informe o endereço de retirada (origem).');
      return;
    }

    if (!entrega.trim()) {
      setErrorMessage('Informe o endereço de entrega (destino).');
      return;
    }

    if (!nomeSignatario.trim()) {
      setErrorMessage('Informe o nome do responsável pela assinatura da solicitação.');
      return;
    }

    // Validação de Saldo e Bloqueio da Proposta:
    if (selectedProposalOption?.pedidoId) {
      const currentSaldo = await propostaSaldoService.getSaldoProposta(selectedProposalOption.pedidoId);
      const isBloqueado = await propostaSaldoService.isBloqueioSemSaldoAtivo();

      // Bloqueio se a cota geral da proposta estiver esgotada (saldo 0)
      if (
        (isBloqueado || currentSaldo?.alertaBloqueadoGeral) &&
        currentSaldo &&
        currentSaldo.totalContratado > 0 &&
        currentSaldo.saldoRestante <= 0
      ) {
        setErrorMessage(
          `Solicitação Bloqueada: A Proposta #${selectedProposalOption.propostaNumero} consumiu a cota total de ${currentSaldo.totalContratado} fretes contratados (${currentSaldo.totalUtilizado} já realizados, Saldo: 0). Conforme a política da empresa, novas solicitações estão bloqueadas para propostas sem saldo. Entre em contato com a equipe comercial para aditivo ou renovação.`
        );
        return;
      }

      // Bloqueio se o item específico selecionado estiver sem saldo
      if (selectedProposalOption.detalheId && currentSaldo) {
        const item = currentSaldo.itens.find((it) => String(it.id) === String(selectedProposalOption.detalheId));
        if (item && item.quantidadeContratada > 0 && item.saldoRestante <= 0) {
          setErrorMessage(
            `Solicitação Bloqueada: O veículo/serviço "${item.servicos}" da Proposta #${selectedProposalOption.propostaNumero} já atingiu a cota de ${item.quantidadeContratada} viagens contratadas (${item.quantidadeUtilizada} utilizadas). Escolha outro veículo com saldo disponível ou solicite um aditivo contratual.`
          );
          return;
        }
      }
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const os = await areaClienteService.solicitarFrete({
        pessoaId: clienteAtual.id,
        pedidoId: selectedProposalOption?.pedidoId || null,
        dataAtendimento,
        horarioInicio,
        horarioFinal,
        descricao: descricao.trim() || (selectedProposalOption ? `Transporte ${selectedProposalOption.nomeVeiculo}` : 'Solicitação de Frete'),
        retirada: retirada.trim(),
        entrega: entrega.trim(),
        tipoVeiculoId: selectedProposalOption?.tipoVeiculoId || null,
        quantidadeAjudante: Number(quantidadeAjudante) || 0,
        detalhesAdicional: detalhesAdicional.trim() || (selectedProposalOption ? `Item Proposta #${selectedProposalOption.propostaNumero}` : undefined),
        observacoes: observacoes.trim() || undefined,
        assinatura: signatureBase64,
        rg_assinatura: documentoSignatario.trim() || null,
        nome_signatario: nomeSignatario.trim() || null
      });

      setCreatedOS(os);
      await refreshData();
    } catch (err: any) {
      console.error('Erro ao enviar solicitação de frete:', err);
      setErrorMessage(err?.message || 'Ocorreu um erro ao registrar sua solicitação. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCreatedOS(null);
    setDescricao('');
    setRetirada('');
    setEntrega('');
    setSelectedProposalOption(null);
    setSelectedOptionKey('');
    setQuantidadeAjudante(0);
    setDetalhesAdicional('');
    setObservacoes('');
    setSignatureBase64(null);
    setErrorMessage(null);
  };

  // If successfully created, display clean confirmation screen
  if (createdOS) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-8 text-center relative">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/40">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <span className="px-3 py-1 bg-white/20 text-white text-xs font-black uppercase tracking-wider rounded-full">
            Status: Solicitado (Em Aberto)
          </span>
          <h2 className="text-2xl sm:text-3xl font-black mt-3">Frete Solicitado e Assinado com Sucesso!</h2>
          <p className="text-emerald-100 text-xs sm:text-sm mt-1 max-w-md mx-auto">
            Sua solicitação foi registrada com o status <strong className="text-white">Em Aberto</strong> e com assinatura digital validada.
          </p>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <span className="text-xs font-bold text-slate-500 uppercase">Número da Ordem de Serviço</span>
              <span className="text-lg font-black text-slate-900">#{createdOS.id}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-400 font-medium">Cliente:</span>
                <p className="font-bold text-slate-800">{clienteAtual?.nome}</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Veículo / Proposta:</span>
                <p className="font-bold text-slate-800">
                  {selectedProposalOption?.nomeVeiculo || 'Conforme Proposta'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Data Agendada:</span>
                <p className="font-bold text-slate-800">
                  {createdOS.data_atendimento ? new Date(createdOS.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Horário:</span>
                <p className="font-bold text-slate-800">{createdOS.horario_inicio} às {createdOS.horario_final}</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Responsável / Assinatura:</span>
                <p className="font-bold text-slate-800">{nomeSignatario} {documentoSignatario ? `(${documentoSignatario})` : ''}</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Ajudantes Solicitados:</span>
                <p className="font-bold text-slate-800">{createdOS.quantidade_ajudante || 0} ajudante(s)</p>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200 text-xs space-y-1">
              <div className="flex items-start gap-1.5 text-slate-700">
                <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                <span><strong>Retirada:</strong> {createdOS.retirada}</span>
              </div>
              <div className="flex items-start gap-1.5 text-slate-700">
                <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <span><strong>Entrega:</strong> {createdOS.entrega}</span>
              </div>
            </div>

            {signatureBase64 && (
              <div className="pt-3 border-t border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-400">Assinatura Digital Registrada:</span>
                <div className="mt-1 p-2 bg-white rounded-xl border border-slate-200 inline-block">
                  <img src={signatureBase64} alt="Assinatura" className="max-h-12 object-contain" referrerPolicy="no-referrer" />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/area-cliente/os"
              className="w-full sm:flex-1 py-3 px-4 bg-slate-900 hover:bg-black text-white font-bold text-xs rounded-xl text-center transition-colors flex items-center justify-center gap-2"
            >
              <Truck className="w-4 h-4" />
              <span>Ver Minhas Ordens de Serviço</span>
            </Link>
            <button
              onClick={resetForm}
              className="w-full sm:w-auto py-3 px-5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Solicitar Outro Frete</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-red-50 text-red-600 rounded-xl">
            <PlusCircle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900">Solicitar Novo Frete</h1>
            <p className="text-xs text-slate-500">
              Selecione o veículo da sua proposta comercial, defina a rota e assine digitalmente sua solicitação.
            </p>
          </div>
        </div>

        <div className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs flex items-center gap-2 text-slate-700">
          <Building2 className="w-4 h-4 text-red-600" />
          <span className="font-semibold">{clienteAtual?.nome}</span>
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start space-x-3 text-xs text-red-800 animate-in fade-in">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Atenção ao preencher o formulário</p>
            <p className="mt-0.5">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Form Inputs (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* 1. Agendamento & Serviço */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center space-x-2 border-b border-slate-100 pb-3">
              <Calendar className="w-4 h-4 text-red-600" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">1. Data, Horário e Carga</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Data de Atendimento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={dataAtendimento}
                  onChange={(e) => setDataAtendimento(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Horário Previsto Início</label>
                <input
                  type="time"
                  value={horarioInicio}
                  onChange={(e) => setHorarioInicio(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Horário Limite Final</label>
                <input
                  type="time"
                  value={horarioFinal}
                  onChange={(e) => setHorarioFinal(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Descrição do Material / Carga / Serviço
              </label>
              <input
                type="text"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex: Transporte de tubulações, pallets de mercadorias, caixas..."
                className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* 2. Rota e Endereços */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center space-x-2 border-b border-slate-100 pb-3">
              <MapPin className="w-4 h-4 text-red-600" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">2. Rota de Transporte</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-600"></span>
                  Endereço Completo de Retirada (Origem) <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={2}
                  value={retirada}
                  onChange={(e) => setRetirada(e.target.value)}
                  placeholder="Rua, Número, Bairro, Cidade - SP, CEP, Ponto de referência e contato no local..."
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  Endereço Completo de Entrega (Destino) <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={2}
                  value={entrega}
                  onChange={(e) => setEntrega(e.target.value)}
                  placeholder="Rua, Número, Bairro, Cidade - SP, CEP, Responsável pelo recebimento..."
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>

          {/* 3. Veículos da Proposta do Cliente */}
          {clienteAtual && (
            <div>
              <SelecaoVeiculoProposta
                clienteId={clienteAtual.id}
                selectedOptionKey={selectedOptionKey}
                onSelectOption={handleSelectProposalVehicle}
              />

              {/* Banner de Saldo e Consumo de Caminhões da Proposta */}
              {selectedProposalOption && (
                <div className="mt-3">
                  <PropostaSaldoBanner
                    saldo={saldoInfo}
                    loading={loadingSaldo}
                    bloqueioAtivo={bloqueioSemSaldoAtivo || saldoInfo?.alertaBloqueadoGeral}
                    selectedItemServicoId={selectedProposalOption.detalheId ? String(selectedProposalOption.detalheId) : null}
                  />
                </div>
              )}
            </div>
          )}

          {/* 4. Ajudantes e Instruções */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center space-x-2 border-b border-slate-100 pb-3">
              <Users className="w-4 h-4 text-red-600" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">4. Ajudantes & Instruções</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Quantidade de Ajudantes
                </label>
                <select
                  value={quantidadeAjudante}
                  onChange={(e) => setQuantidadeAjudante(Number(e.target.value))}
                  className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="0">Sem Ajudante (Apenas Motorista)</option>
                  <option value="1">1 Ajudante</option>
                  <option value="2">2 Ajudantes</option>
                  <option value="3">3 Ajudantes</option>
                  <option value="4">4 Ajudantes</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Observações Especiais / Restrições do Local
                </label>
                <textarea
                  rows={2}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex: Exigência de EPI, entrada até as 11h, altura máxima de 2.80m..."
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>

          {/* 5. Campo para o Cliente Assinar */}
          <AssinaturaDigitalPad
            onSignatureChange={handleSignatureChange}
            initialNome={clienteAtual?.nome || ''}
            initialDoc={clienteAtual?.documento || ''}
            label="5. Assinatura Digital do Cliente"
            required={true}
          />
        </div>

        {/* Summary Sidebar (1 col) */}
        <div className="space-y-6">
          <div className="bg-slate-900 text-white p-6 rounded-3xl shadow-xl border border-slate-800 space-y-5 sticky top-24">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-red-600/30 text-red-300 border border-red-500/40 rounded-full text-[11px] font-bold">
                <Sparkles className="w-3 h-3" />
                <span>Resumo da Solicitação</span>
              </div>
              <h3 className="text-lg font-black mt-2">Confirmação Prévia</h3>
              <p className="text-[11px] text-slate-400">
                Verifique os dados antes de assinar e confirmar o pedido.
              </p>
            </div>

            <div className="space-y-3 text-xs bg-slate-800/80 p-4 rounded-2xl border border-slate-700/60 divide-y divide-slate-700/60">
              <div className="pb-2">
                <span className="text-slate-400 text-[10px] uppercase font-bold">Veículo Contratado:</span>
                <p className="font-bold text-white text-sm">
                  {selectedProposalOption?.nomeVeiculo || 'A definir na proposta'}
                </p>
                {selectedProposalOption && (
                  <p className="text-emerald-400 text-[11px]">Proposta Comercial #{selectedProposalOption.propostaNumero}</p>
                )}
              </div>

              <div className="py-2">
                <span className="text-slate-400 text-[10px] uppercase font-bold">Data Prevista:</span>
                <p className="font-bold text-white text-sm">
                  {dataAtendimento ? new Date(dataAtendimento + 'T00:00:00').toLocaleDateString('pt-BR') : 'Não informada'}
                </p>
                <p className="text-slate-400 text-[11px]">Horário: {horarioInicio} às {horarioFinal}</p>
              </div>

              <div className="py-2 space-y-1">
                <span className="text-slate-400 text-[10px] uppercase font-bold">Origem:</span>
                <p className="text-slate-200 truncate">{retirada || 'Não informada'}</p>
              </div>

              <div className="py-2 space-y-1">
                <span className="text-slate-400 text-[10px] uppercase font-bold">Destino:</span>
                <p className="text-slate-200 truncate">{entrega || 'Não informado'}</p>
              </div>

              <div className="py-2 flex items-center justify-between text-slate-300">
                <span>Ajudantes:</span>
                <span className="font-bold text-white">{quantidadeAjudante}</span>
              </div>

              {selectedProposalOption && saldoInfo && (
                <div className="py-2 flex items-center justify-between text-slate-300">
                  <span>Saldo da Proposta:</span>
                  <span
                    className={`font-bold ${
                      saldoInfo.statusSaldo === 'esgotado'
                        ? 'text-red-400'
                        : saldoInfo.statusSaldo === 'alerta'
                        ? 'text-amber-400'
                        : 'text-emerald-400'
                    }`}
                  >
                    {saldoInfo.statusSaldo === 'sem_cota'
                      ? 'Sem cota fixa'
                      : `${saldoInfo.saldoRestante} de ${saldoInfo.totalContratado} restantes`}
                  </span>
                </div>
              )}

              <div className="pt-2 flex items-center justify-between text-slate-300">
                <span>Assinatura Digital:</span>
                <span className={`font-bold ${signatureBase64 || nomeSignatario ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {signatureBase64 || nomeSignatario ? '✓ Pronta' : 'Pendente'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  (bloqueioSemSaldoAtivo && saldoInfo?.statusSaldo === 'esgotado')
                }
                className="w-full py-3.5 px-4 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded-xl shadow-lg shadow-red-600/40 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Enviando e Registrando...</span>
                  </>
                ) : bloqueioSemSaldoAtivo && saldoInfo?.statusSaldo === 'esgotado' ? (
                  <span>Bloqueado por Falta de Saldo</span>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4" />
                    <span>Assinar e Confirmar Solicitação</span>
                  </>
                )}
              </button>

              <p className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>Status imediato: Solicitado / Em Aberto</span>
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

